import { useCallback, useMemo, useState } from 'react';

import {
  ActivityMatrixModel,
  ActivitySort,
  ActivitySortKey,
} from '../../@types/service-activity.type';
import {
  filterActivityMatrix,
  nextSortState,
  sortActivityMatrix,
} from '../../utils/service-activity-view';
import { useDebouncedValue } from '../../utils/use-debounced-value';

const SEARCH_DEBOUNCE_MS = 200;

/**
 * Search, sort and collapse state for the matrix, layered over the pure
 * builders so each stage only recomputes when its own input changes.
 */
export const useActivityMatrixView = (matrix: ActivityMatrixModel) => {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<ActivitySort | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<ReadonlySet<string>>(new Set());

  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const filtered = useMemo(
    () => filterActivityMatrix(matrix, debouncedSearch),
    [matrix, debouncedSearch]
  );
  const view = useMemo(() => sortActivityMatrix(filtered, sort), [filtered, sort]);

  const handleSort = useCallback((key: ActivitySortKey) => {
    setSort((previous) => nextSortState(previous, key));
  }, []);

  const handleToggleGroup = useCallback((groupKey: string) => {
    setCollapsedGroups((previous) => {
      const next = new Set(previous);
      if (next.has(groupKey)) next.delete(groupKey);
      else next.add(groupKey);
      return next;
    });
  }, []);

  // Count only the groups actually on screen. Using `collapsedGroups.size`
  // compared groups hidden by the search (or gone from the payload) against the
  // visible count, so collapsing three groups then searching down to one showed
  // "Expand all" while that one was still expanded.
  const visibleCollapsedCount = useMemo(
    () => view.groups.reduce((count, group) => count + (collapsedGroups.has(group.key) ? 1 : 0), 0),
    [view.groups, collapsedGroups]
  );

  const allCollapsed = view.groups.length > 0 && visibleCollapsedCount >= view.groups.length;

  const handleToggleAll = useCallback(() => {
    setCollapsedGroups((previous) => {
      const next = new Set(previous);
      // Only touch visible groups, so a hidden group keeps its own state.
      if (allCollapsed) view.groups.forEach((group) => next.delete(group.key));
      else view.groups.forEach((group) => next.add(group.key));
      return next;
    });
  }, [view.groups, allCollapsed]);

  return {
    search,
    /** The term the visible results actually reflect. Use this in result copy
     *  so the message can't quote a query the filter hasn't applied yet. */
    appliedSearch: debouncedSearch,
    setSearch,
    sort,
    handleSort,
    collapsedGroups,
    handleToggleGroup,
    allCollapsed,
    handleToggleAll,
    view,
    /** True when a search is active but matched nothing. */
    isFilteredEmpty: view.groups.length === 0 && debouncedSearch.trim().length > 0,
  };
};
